import { useContext } from 'react';
import { ClientConfigContext } from '../../clientConfigContext';
import { useAccessToken } from '../../hooks/useAccessToken';
import {
    authorizeByActivationCode,
    authorizeByDemographics,
    authorizeByIdentityVerification,
    authorizeByInstantActivation,
    buildStandardHeaders,
} from './pimsClient';
import { PimsApi, PimsApiCall, PimsApiConfig } from './types';

/**
 * Returns a PIMS API function using standard headers and an access token.
 * @param accessToken The JWT
 * @param pimsApiUrl The full URL to the PIMS API
 * @param method GET or POST
 * @param path The path to the PIMS API endpoint
 * @param fn The handling function
 * @returns A function that sends a request to the PIMS API
 */
const getPimsAPICall = <PimsApiCallArgs, PimsApiCallResponse>(
    accessToken: string | undefined,
    pimsApiUrl: string,
    method: 'POST' | 'GET',
    path: string,
    fn: PimsApiCall<PimsApiCallArgs, PimsApiCallResponse>,
) => {
    return (args: PimsApiCallArgs): Promise<PimsApiCallResponse> => {
        if (!accessToken) {
            throw new Error('Attempting to call PIMS API without an access token');
        }
        const headers: Record<string, string> = buildStandardHeaders(accessToken);
        const url = new URL(`${pimsApiUrl}${path}`);

        return fn(url, method, headers, args);
    };
};

/* PIMS API supported by the usePIMS hook */
const pimsAPI: PimsApiConfig = {
    authorizeByActivationCode: {
        path: '/v2/consumer/authorization/access-code',
        method: 'POST',
        fn: authorizeByActivationCode,
    },
    authorizeByDemographics: {
        path: '/v2/consumer/authorization/identity-verification/prove',
        method: 'POST',
        fn: authorizeByDemographics,
    },
    authorizeByIdentityVerification: {
        path: '/v2/consumer/authorization/identity-verification',
        method: 'POST',
        fn: authorizeByIdentityVerification,
    },
    authorizeByInstantActivation: {
        path: '/v2/consumer/authorization/embedded-code',
        method: 'POST',
        fn: authorizeByInstantActivation,
    },
};

/**
 * Returns an easy-to-use PIMS API object that can be used to calls to the PIMS API.
 */
const usePIMS = (): PimsApi => {
    const { accessToken } = useAccessToken();
    const { pimsApiUrl } = useContext(ClientConfigContext);

    return {
        isReady: !!accessToken && !!pimsApiUrl,
        authorizeByActivationCode: getPimsAPICall(
            accessToken,
            pimsApiUrl,
            pimsAPI.authorizeByActivationCode.method,
            pimsAPI.authorizeByActivationCode.path,
            pimsAPI.authorizeByActivationCode.fn,
        ),
        authorizeByDemographics: getPimsAPICall(
            accessToken,
            pimsApiUrl,
            pimsAPI.authorizeByDemographics.method,
            pimsAPI.authorizeByDemographics.path,
            pimsAPI.authorizeByDemographics.fn,
        ),
        authorizeByIdentityVerification: getPimsAPICall(
            accessToken,
            pimsApiUrl,
            pimsAPI.authorizeByIdentityVerification.method,
            pimsAPI.authorizeByIdentityVerification.path,
            pimsAPI.authorizeByIdentityVerification.fn,
        ),
        authorizeByInstantActivation: getPimsAPICall(
            accessToken,
            pimsApiUrl,
            pimsAPI.authorizeByInstantActivation.method,
            pimsAPI.authorizeByInstantActivation.path,
            pimsAPI.authorizeByInstantActivation.fn,
        ),
    };
};

export { usePIMS };
